import { Button, Center, Loader, Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { client, configureApi, isApiError, type Me, unwrap } from "@/api";

import { initialSession, meOf, type SessionState, sessionReducer, tokenOf } from "./session-state";
import { tokenStore } from "./token";

type SessionApi = {
  state: SessionState;
  me: Me | null;
  login: (email: string, password: string) => Promise<void>;
  verifyTotp: (code: string) => Promise<void>;
  /** Re-reads `auth/me` (after enrolment, profile edits, TOTP_* state drift). */
  refreshMe: () => Promise<void>;
  logout: () => Promise<void>;
  /** Abandons a pending 2FA challenge (back to the password step). */
  cancelLogin: () => void;
};

const SessionContext = createContext<SessionApi | null>(null);

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}

export const useMe = (): Me | null => useSession().me;

/** Only same-origin paths survive as `?next=` (no open redirect). */
export function safeNext(next: string | null): string | null {
  return next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/login") ? next : null;
}

export const homeOf = (me: Me | null): string => (me?.user_type === "PORTAL" ? "/portal" : "/admin");

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(sessionReducer, null, () => initialSession(tokenStore.load()));
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Layout effect: the token must be in place before any child query fires (passive effects).
  const token = tokenOf(state);
  useLayoutEffect(() => tokenStore.set(token), [token]);

  const signOut = useCallback(
    (notice?: string) => {
      tokenStore.set(null);
      dispatch({ type: "signed_out", notice });
      queryClient.clear();
    },
    [queryClient],
  );

  useLayoutEffect(() => {
    configureApi({
      getToken: tokenStore.get,
      onSessionExpired: () => {
        if (!tokenStore.get()) return;
        signOut("Your session has ended. Sign in again.");
        const here = window.location.pathname + window.location.search;
        navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
      },
    });
  }, [navigate, signOut]);

  const restore = useCallback(() => {
    setRestoreError(null);
    unwrap(client.GET("/api/v1/auth/me"))
      .then((me) => dispatch({ type: "me", me }))
      .catch((e: unknown) => {
        if (isApiError(e) && e.status === 401) signOut();
        else setRestoreError(isApiError(e) ? e.message : "Could not reach the server");
      });
  }, [signOut]);

  useLayoutEffect(() => {
    if (state.status === "restoring") restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on load
  }, []);

  const api = useMemo<SessionApi>(
    () => ({
      state,
      me: meOf(state),
      async login(email, password) {
        const res = await unwrap(client.POST("/api/v1/auth/login", { body: { email, password } }));
        if (res.token) tokenStore.set(res.token);
        dispatch({ type: "login", res });
      },
      async verifyTotp(code) {
        if (state.status !== "password_ok_needs_totp") return;
        try {
          const res = await unwrap(
            client.POST("/api/v1/auth/login/2fa", { body: { pending_token: state.pendingToken, code } }),
          );
          if (res.token) tokenStore.set(res.token);
          dispatch({ type: "totp_ok", res });
        } catch (e) {
          if (isApiError(e, "PENDING_EXPIRED")) dispatch({ type: "pending_expired" });
          throw e;
        }
      },
      async refreshMe() {
        const me = await unwrap(client.GET("/api/v1/auth/me"));
        dispatch({ type: "me", me });
      },
      cancelLogin: () => signOut(),
      async logout() {
        await unwrap(client.POST("/api/v1/auth/logout")).catch(() => undefined);
        tokenStore.set(null);
        queryClient.clear();
        // Full reload: drops every in-memory value (query cache, component state) and avoids the guard's ?next=.
        window.location.replace("/login");
      },
    }),
    [state, signOut, queryClient],
  );

  if (state.status === "restoring") {
    return (
      <Center h="100vh">
        {restoreError ?
          <Stack align="center">
            <Text>{restoreError}</Text>
            <Button onClick={restore}>Retry</Button>
          </Stack>
        : <Loader />}
      </Center>
    );
  }
  return <SessionContext.Provider value={api}>{children}</SessionContext.Provider>;
}
